
import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ContactFormValues } from './ContactFormSchema';

export const useContactForm = () => {
  const { toast } = useToast();
  const [contactMethod, setContactMethod] = useState<string>("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  async function uploadAttachment(): Promise<string | null> {
    if (!selectedFile) return null;
    
    setFileUploading(true);
    
    try {
      // Create a unique filename
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${fileName}`;
      
      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('contact_attachments')
        .upload(filePath, selectedFile);
      
      if (error) {
        throw error;
      }
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('contact_attachments')
        .getPublicUrl(filePath);
      
      return publicUrl;
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload failed",
        description: "We couldn't upload your file. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setFileUploading(false);
    }
  }

  async function onSubmit(data: ContactFormValues) {
    setIsSubmitting(true);
    
    try {
      // Upload attachment if selected
      let attachmentUrl = null;
      if (selectedFile) {
        attachmentUrl = await uploadAttachment();
        if (!attachmentUrl && selectedFile) {
          // If file upload failed but was selected, return early
          setIsSubmitting(false);
          return;
        }
      }
      
      // Prepare the contact message data
      const messageData = {
        name: data.name,
        company: data.company || null,
        email: data.email,
        phone: data.phone || null,
        source: data.source,
        message: data.message,
        attachment_url: attachmentUrl,
      };
      
      // Insert the contact message into the Supabase database
      const { error: dbError } = await supabase
        .from('contact_messages')
        .insert([messageData]);
      
      if (dbError) {
        throw dbError;
      }
      
      // Call the edge function to send confirmation email
      const { error: emailError } = await supabase.functions.invoke('send-confirmation', {
        body: {
          name: data.name,
          email: data.email
        }
      });
      
      if (emailError) {
        console.warn("Error sending confirmation email:", emailError);
        // We'll continue even if email sending fails
      }
      
      // Show success toast and reset form
      toast({
        title: "Message sent!",
        description: `Thanks ${data.name.split(' ')[0]}, I'll get back to you soon.`,
      });
      
      // Update UI state
      setFormSubmitted(true);
      setSelectedFile(null);
      
      // Log analytics
      console.log("Contact form submitted:", {
        timestamp: new Date().toISOString(),
        source: data.source
      });
      
      // Reset the submitted state after 30 seconds
      setTimeout(() => {
        setFormSubmitted(false);
      }, 30000);
      
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Something went wrong",
        description: "Your message couldn't be sent. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    contactMethod,
    setContactMethod,
    isSubmitting,
    formSubmitted,
    setFormSubmitted,
    fileUploading,
    selectedFile,
    setSelectedFile,
    onSubmit,
  };
};
